import { formatNumber } from '@sortlist-frontend/utils';
import { useState } from 'react';

import type { GuideField, GuideLabels } from '../types';
import { BarTrack } from './BarTrack';
import { NumberField } from './NumberField';

interface RoiCalculatorProps {
  fields: readonly GuideField[];
  outputs: { hoursMonth: string; hoursYear: string; gain: string };
  locale: string;
  labels: Pick<GuideLabels, 'roiEmptyCost' | 'roiNegative' | 'roiPayback' | 'hours'>;
}

const numberOf = (value: string | undefined) => Math.max(0, Number(value) || 0);

export const RoiCalculator = (props: RoiCalculatorProps) => {
  const { fields, outputs, locale, labels } = props;
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.id, field.value ?? ''])),
  );
  const cases = numberOf(values['roi-n']);
  const minutes = numberOf(values['roi-m']);
  const share = Math.min(100, numberOf(values['roi-p']));
  const rate = numberOf(values['roi-r']);
  const running = numberOf(values['roi-u']);
  const setup = numberOf(values['roi-c']);
  const hoursMonth = (cases * minutes * (share / 100)) / 60;
  const gainMonth = hoursMonth * rate - running;
  const payback =
    setup <= 0
      ? labels.roiEmptyCost
      : gainMonth <= 0
        ? labels.roiNegative
        : labels.roiPayback.replace(
            '{months}',
            formatNumber({ value: Math.round((setup / gainMonth) * 10) / 10, locale }),
          );
  const ratio = setup > 0 && gainMonth > 0 ? Math.min(1, (12 / (setup / gainMonth)) * 0.5) : 0;
  const hours = (value: number) => `${formatNumber({ value: Math.round(value), locale })} ${labels.hours}`;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1.2fr_1fr]">
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((field) => (
          <NumberField
            key={field.id}
            field={field}
            value={values[field.id] ?? ''}
            onChange={(value) => setValues({ ...values, [field.id]: value })}
          />
        ))}
      </div>
      <div
        className="rounded-2xl border border-primary-200 bg-primary-50 p-5 md:p-6 lg:sticky lg:top-24"
        aria-live="polite">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-secondary-700">{outputs.hoursMonth}</p>
            <p className="text-2xl font-extrabold text-secondary-950">{hours(hoursMonth)}</p>
          </div>
          <div>
            <p className="text-sm text-secondary-700">{outputs.hoursYear}</p>
            <p className="text-2xl font-extrabold text-secondary-950">{hours(hoursMonth * 12)}</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-secondary-700">{outputs.gain}</p>
        <p className="text-2xl font-extrabold text-secondary-950">
          {formatNumber({ value: Math.round(gainMonth * 12), locale })} €
        </p>
        <div className="mt-4">
          <BarTrack label={outputs.gain} ratio={ratio} />
        </div>
        <p className="mt-2 text-sm leading-relaxed text-secondary-700">{payback}</p>
      </div>
    </div>
  );
};
